import { useEffect } from 'react';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';


interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional sub-heading under the title. */
  description?: string;
  /** Max-width utility class. Defaults to max-w-md. */
  sizeClass?: string;
  /** Main content. */
  children: React.ReactNode;
  /** Footer (buttons). Rendered right-aligned with 2-col gap. If omitted,
   *  the caller must embed their own action row in `children`. */
  footer?: React.ReactNode;
  /** Close when the user clicks the backdrop. Default false so forms
   *  don't lose unsaved input accidentally. */
  closeOnBackdrop?: boolean;
}


/** Shared dialog overlay used across the compliance portal. Closes on
 *  Escape; backdrop-click dismiss is opt-in. Tailwind-only, no portal
 *  because `fixed inset-0 z-50` already escapes ancestor stacking. */
export default function Modal({
  open,
  onClose,
  title,
  description,
  sizeClass = 'max-w-md',
  children,
  footer,
  closeOnBackdrop = false,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Alt+C closes, alongside the Escape handler above. This is a shell — the
  // consuming modal owns the footer buttons, so it binds its own Save and, being
  // this component's parent, claims the layer after us and wins. Our claim is the
  // floor for consumers that add no chords of their own.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white dark:bg-gray-800 rounded-lg w-full ${sizeClass} border border-gray-200 dark:border-gray-700 shadow-xl`}
      >
        <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="p-6">
          {description && (
            <p className="text-sm text-muted mb-3">
              {description}
            </p>
          )}
          <div className="mt-2">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 mt-4">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );
}
